import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { useParams } from 'react-router';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useMemo } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import React from 'react';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { RubricBankItemForWrite } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-item';
import { DatabaseRubricBankItemsView } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRubricBankItemsView';
import { useRubricItemsData } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-items-data';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const DatabaseRubricBankItemsPage = () => {
  const { organizationId, databaseId } = useParams();

  if (!organizationId || !databaseId) {
    throw new Error(
      'DatabaseRubricBankPage: organizationId or databaseId is not defined',
    );
  }

  const useRubricItemsDataPayload = useRubricItemsData({
    organizationId,
    databaseId,
    singleSelectConfig: null,
  });

  const [deleteItemIdSignal, setDeleteItemIdSignal] = useState<string | null>(
    null,
  );

  const deleteItemPatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/rubric-bank-constructs/${deleteItemIdSignal}`;
  }, [organizationId, databaseId, deleteItemIdSignal]);

  const deleteItemPattern = useSimpleWritePattern({
    getUrlFn: deleteItemPatternGetUrlFn,
    method: 'DELETE',
    canWrite: true,
    writeSchema: EmptyObject,
    responseSchema: EmptyObject,
  });

  const isLoading = useMemo(() => {
    return (
      useRubricItemsDataPayload.readPattern.status === 'loading' ||
      useRubricItemsDataPayload.createItemPattern.status === 'saving'
    );
  }, [useRubricItemsDataPayload]);

  const errorMessage = useMemo(() => {
    return useRubricItemsDataPayload.readPattern.status === 'error'
      ? useRubricItemsDataPayload.readPattern.error.errorType
      : '';
  }, [useRubricItemsDataPayload]);

  const deleteConstruct = useCallback(() => {
    if (deleteItemPattern.status !== 'saving') {
      deleteItemPattern.saveEntity({}, () => {
        setDeleteItemIdSignal(null);
        if (useRubricItemsDataPayload.readPattern.status !== 'loading') {
          useRubricItemsDataPayload.readPattern.reloadEntity();
        }
      });
    }
  }, [deleteItemPattern, useRubricItemsDataPayload]);

  useEffect(() => {
    if (deleteItemIdSignal !== null) {
      deleteConstruct();
    }
  }, [deleteItemIdSignal, deleteConstruct]);

  return (
    <AdminLayout currentPage={'DatabaseRubricBankItems'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Edit Database',
            to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
          },
          {
            label: 'Rubric Bank',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank`,
          },
          {
            label: 'Rubric Bank Items',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank-items`,
          },
        ]}
      />
      <Spacing size={'md'} direction={'vertical'}>
        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (
                useRubricItemsDataPayload.createItemPattern.status === 'saving'
              ) {
                return;
              }

              useRubricItemsDataPayload.createItemPattern.saveEntity(
                {
                  name: 'New Construct',
                  createdAt: Date.now(),
                  updatedAt: null,
                  createdBy: 'Me',
                  criteria: [],
                },
                () => {
                  if (
                    useRubricItemsDataPayload.readPattern.status !== 'loading'
                  ) {
                    useRubricItemsDataPayload.readPattern.reloadEntity();
                  }
                },
              );
            },
          }}
          size={'small'}
          disabled={isLoading}
          variant={'primary'}
          text={'Create New Item'}
        />

        <DatabaseRubricBankItemsView
          payload={useRubricItemsDataPayload}
          isEditable={true}
        />
      </Spacing>
    </AdminLayout>
  );
};
